import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import type { Question, EnneagramType } from "@/types/vocational"
import { useDelayGate } from "@/hooks/useDelayGate"
import { useState } from "react"

interface ReflectionPresenterProps {
  question: Question
  delayMs?: number
  onContinue: () => void
}

const DEFAULT_DELAY_MS = 6000

export function ReflectionPresenter({
  question,
  delayMs = DEFAULT_DELAY_MS,
  onContinue,
}: ReflectionPresenterProps) {
  const gateKey = `${question.id}-reflection`
  const { ready } = useDelayGate(delayMs, gateKey)
  const options = question.options ?? []
  const hasOptions = options.length > 0
  const [selectedOption, setSelectedOption] = useState<number | undefined>(undefined)

  // Split question by double newline: title first, rest (including checkpoint) second
  const titleText = question.question.split('\n\n')[0]
  const bodyText = question.question.split('\n\n').slice(1).join('\n\n')

  return (
    <Card className="w-full max-w-2xl mx-auto p-6 md:p-8 shadow-card text-center">
      <div className="space-y-6">
        <div className="space-y-4">
          {titleText && (
            <h2 className="text-lg md:text-xl font-semibold text-title-blue">
              {titleText}
            </h2>
          )}
          {bodyText && (
            <p className="text-base text-foreground whitespace-pre-line">
              {bodyText}
            </p>
          )}
        </div>

        {hasOptions && (
          <div className="flex gap-8 justify-center items-start flex-wrap">
            {options.map((option) => {
              const isSelected = selectedOption === option.value
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setSelectedOption(option.value)}
                  className={`
                    flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition-all
                    ${isSelected
                      ? 'border-title-blue bg-title-blue/10'
                      : 'border-border hover:border-title-blue/50 hover:bg-accent/20'
                    }
                  `}
                >
                  <div className={`
                    h-6 w-6 rounded-full border-2 flex items-center justify-center transition-all
                    ${isSelected ? 'border-title-blue bg-title-blue' : 'border-gray-300'}
                  `}>
                    {isSelected && (
                      <div className="h-2.5 w-2.5 rounded-full bg-white" />
                    )}
                  </div>
                  <span className="text-sm text-foreground text-center">
                    {option.text}
                  </span>
                </button>
              )
            })}
          </div>
        )}

        <Button
          onClick={onContinue}
          disabled={!ready}
          className="w-full bg-gradient-primary hover:opacity-90 transition-opacity"
        >
          Continuar
        </Button>
      </div>
    </Card>
  )
}
